import { message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import { DELETE_ERROR, FETCH_ERROR, REQUEST_LOADING } from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { RolesService } from 'services/RolesService';
import { formatDate } from 'utils';
import { RolesTable } from './RolesTable';

function Roles() {
  useDocumentTitle('Роли');
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const {
    state: { drawerVisible, activeRecord },
    openDrawer,
    closeDrawer,
  } = useDrawers();
  const { page, perPage, totalRows, setPagination, setTotalRows } =
    usePagination();

  const { setCount } = useHeaderChanger('Роли');

  const fetchRoles = useCallback(async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await RolesService.getRoles(page, perPage);

      setRoles(items);
      setCount(total);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  }, [page, perPage]);

  useEffect(() => fetchRoles(), [fetchRoles]);

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await RolesService.deleteRole(activeRecord?.id);

      if (isDeleted) {
        closeDrawer();
        fetchRoles();
        message.success('Роль успешно удалена');
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader
        onCreate={() => navigate('/setting/access-manage/roles/create')}
        pageId="roles"
      />

      <RolesTable
        items={roles}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.name}
        pageId="roles"
        recordFields={[
          { label: 'Наименование', key: 'name' },
          { label: 'Описание', key: 'description' },
          { label: 'Код', key: 'slug' },
          {
            label: 'Дата создания',
            key: 'created',
            computedValue: formatDate(activeRecord?.created),
          },
          {
            label: 'Дата обновления',
            key: 'updated',
            computedValue: formatDate(activeRecord?.updated),
          },
          {
            label: 'Статус',
            key: 'active',
            computedValue: activeRecord?.active ? 'Активный' : 'Не активный',
          },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onDelete={handleDelete}
        onEdit={() =>
          navigate(`/setting/access-manage/roles/edit/${activeRecord.id}`)
        }
        deleteConfirmText="Вы точно хотите удалить эту роль ?"
      />
    </>
  );
}

export { Roles };
